'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { useCertificates } from '@/contexts/CertificatesContext';
import { useSeafarerProfile } from '@/contexts/SeafarerProfileContext';
import { AlertTriangle, CheckCircle2, Anchor, Settings2, ChevronDown, ChevronUp } from 'lucide-react';
import { getDepartment, getRequiredCerts, DEPT_LABEL, type Department } from '@/lib/utils/department';
import { getCertificateAttentionItems, type AttentionSeverity } from '@/lib/certificate-alerts';

const RadialChart = dynamic(() => import('./RadialChart'), { ssr: false });

const DEPT_ICON: Record<Department, React.ReactNode> = {
  deck:    <Anchor size={11} />,
  engine:  <Settings2 size={11} />,
  unknown: null,
};

const SEVERITY_CONFIG: Record<AttentionSeverity, { label: string; color: string; bg: string }> = {
  critical: {
    label: 'Critical',
    color: 'var(--status-critical)',
    bg: 'var(--status-critical-bg)',
  },
  warning: {
    label: 'Warning',
    color: 'var(--status-expiring-soon)',
    bg: 'var(--status-expiring-soon-bg)',
  },
  information: {
    label: 'Information',
    color: 'var(--primary)',
    bg: 'var(--secondary)',
  },
};

export default function ProfileCompleteness() {
  const [showMissing, setShowMissing] = useState(false);
  const { certificates } = useCertificates();
  const { data: seafarerData } = useSeafarerProfile();

  const dept = getDepartment(
    seafarerData?.profile?.discipline ?? '',
    seafarerData?.profile?.currentRank  ?? '',
  );

  const requiredCerts = getRequiredCerts(dept);

  const matched = requiredCerts.map((req) => ({
    ...req,
    found: certificates.some((cert) => req.patterns.some((p) => p.test(cert.name))),
  }));

  const foundCount  = matched.filter((r) => r.found).length;
  const total       = requiredCerts.length;
  const percentage  = total > 0 ? Math.round((foundCount / total) * 100) : 0;
  const missing     = matched.filter((r) => !r.found);

  const attentionItems = getCertificateAttentionItems(certificates).slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Checklist coverage card */}
      <div className="card-elevated p-4 xl:p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-700 text-foreground">Certificate Checklist Coverage</h3>
          {dept !== 'unknown' && (
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-700 uppercase tracking-wide"
              style={{
                backgroundColor: dept === 'deck' ? 'var(--status-valid-bg)' : 'color-mix(in srgb, var(--primary) 12%, transparent)',
                color:           dept === 'deck' ? 'var(--status-valid)'    : 'var(--primary)',
              }}
            >
              {DEPT_ICON[dept]}
              {DEPT_LABEL[dept]}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {dept === 'unknown'
            ? 'Based on a common STCW checklist'
            : `Common checklist for ${DEPT_LABEL[dept]} department`}
        </p>

        <RadialChart percentage={percentage} found={foundCount} total={total} />

        {/* Missing list */}
        {missing.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider">Missing: {missing.length}</p>
              <button
                type="button"
                aria-expanded={showMissing}
                aria-controls="missing-certificate-list"
                onClick={() => setShowMissing((value) => !value)}
                className="inline-flex items-center gap-1 text-xs font-600 text-primary hover:opacity-75"
              >
                {showMissing ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {showMissing ? 'Hide Missing' : 'View Missing Certificates'}
              </button>
            </div>
            {showMissing && (
              <div id="missing-certificate-list" className="space-y-1.5 pt-1">
                {missing.map((req) => (
                  <div key={req.label} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: 'var(--status-expiring-soon)' }} />
                    <p className="text-xs text-muted-foreground">{req.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {missing.length === 0 && (
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
            <CheckCircle2 size={14} style={{ color: 'var(--status-valid)' }} />
            <p className="text-xs font-500" style={{ color: 'var(--status-valid)' }}>All checklist certificates found</p>
          </div>
        )}
      </div>

      {/* Urgent attention card */}
      <div className="card-elevated p-4 xl:p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} style={{ color: 'var(--status-critical)' }} />
          <h3 className="text-sm font-700 text-foreground">Needs Attention</h3>
        </div>
        {attentionItems.length === 0 ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} style={{ color: 'var(--status-valid)' }} />
            <p className="text-xs font-500" style={{ color: 'var(--status-valid)' }}>No tracked certificate issues need attention</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {attentionItems.map(({ certificate: cert, severity, reason }) => {
              const severityConfig = SEVERITY_CONFIG[severity];
              return (
                <div key={`urgent-${cert.id}`} className="flex items-start gap-2.5">
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                    style={{ backgroundColor: severityConfig.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <span
                      className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-700 uppercase tracking-wide mb-1"
                      style={{ color: severityConfig.color, backgroundColor: severityConfig.bg }}
                    >
                      {severityConfig.label}
                    </span>
                    <p className="text-xs font-600 text-foreground leading-tight break-words">{cert.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
